import React, { useEffect, useState } from "react";

//dayjs
import dayjs from "dayjs";

//redux
import { connect, useDispatch, useSelector } from "react-redux";

//action
import {
  getAdmins,
  deleteAdmin,
  toggleAdminDisabled,
  updateLarkNotifications,
  resetAdminSignin,
} from "../store/adminManagement/action";

//routing
import { Link } from "react-router-dom";

//MUI
import { Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from "@material-ui/core";

import { permissionError, warning, warningv2, alert } from "../util/Alert";

//pagination
import Pagination from "./Pagination";

//types
import { OPEN_ADMIN_DIALOG } from "../store/adminManagement/types";
import { OPEN_ADMIN_ACCESS_DIALOG } from "../store/adminAccess/types";

//dialog
import AdminDialog from "../component/dialog/AdminDialog";
import AdminAccessDialog from "../component/dialog/AdminAccessDialog";
import TemporaryPassword from "../component/TemporaryPassword";
import swal from "sweetalert";

//image
import Male from "../assets/images/male.png";
import { LARK_NOTIFICATION_TYPES } from "../const/larkNotificationTypes";

const AdminManagement = (props) => {
  const dispatch = useDispatch();

  const hasPermission = useSelector((state) => state.admin.admin.flag);
  const currentAdminId = useSelector((state) => state.admin.admin._id);

  const [data, setData] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [larkNotificationAdmin, setLarkNotificationAdmin] = useState(null);
  const [selectedLarkNotificationTypes, setSelectedLarkNotificationTypes] = useState([]);

  useEffect(() => {
    props.getAdmins();
  }, []);

  const { admins } = useSelector((state) => state.adminManagement);

  useEffect(() => {
    setData(admins);
  }, [admins]);

  const handlePageChange = (pageNumber) => {
    setActivePage(pageNumber);
  };

  const handleRowsPerPage = (value) => {
    setActivePage(1);
    setRowsPerPage(value);
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);
    setActivePage(1);

    if (value) {
      const filtered = admins.filter(
        (admin) =>
          admin.name?.toLowerCase().includes(value) ||
          admin.email?.toLowerCase().includes(value),
      );
      setData(filtered);
    } else {
      setData(admins);
    }
  };

  const handleOpenDialog = (adminData = null) => {
    if (!hasPermission) return permissionError();
    dispatch({ type: OPEN_ADMIN_DIALOG, payload: adminData });
  };

  const handleOpenAccessDialog = (adminData) => {
    if (!hasPermission) return permissionError();
    dispatch({ type: OPEN_ADMIN_ACCESS_DIALOG, payload: adminData });
  };

  const handleDelete = (adminId) => {
    if (!hasPermission) return permissionError();
    if (adminId === currentAdminId) {
      return alert("Warning", "You cannot delete your own account!", "warning");
    }

    const data = warning();
    data.then((isDeleted) => {
      if (isDeleted) {
        props.deleteAdmin(adminId);
      }
    });
  };

  const [issuedPassword, setIssuedPassword] = useState(null);
  const handleResetSignin = (admin) => {
    if (!hasPermission) return permissionError();
    if (admin._id === currentAdminId) {
      return alert(
        "Warning",
        "Use the Security page to change your own password or authenticator.",
        "warning",
      );
    }
    const issue = (username) =>
      props.resetAdminSignin(admin._id, username).then((result) => {
        if (result) setIssuedPassword(result);
        props.getAdmins();
      });
    // Lark-era admins have no username yet: choose one as part of the reset.
    if (!admin.username) {
      return swal({
        title: `Username for ${admin.name || "this admin"}`,
        text: "3-32 characters: letters, numbers, dot, dash or underscore.",
        content: { element: "input", attributes: { placeholder: "e.g. juan.dc" } },
        buttons: ["Cancel", "Issue temporary password"],
      }).then((username) => {
        if (username && username.trim()) issue(username.trim());
      });
    }
    warningv2(
      "Reset sign-in?",
      "This admin is signed out everywhere and must set a new password and register a new authenticator with the temporary password you give them.",
    ).then((confirmed) => {
      if (confirmed) issue();
    });
  };

  const handleToggleDisabled = (adminId) => {
    if (!hasPermission) return permissionError();
    if (adminId === currentAdminId) {
      return alert(
        "Warning",
        "You cannot disable your own account!",
        "warning",
      );
    }
    props.toggleAdminDisabled(adminId);
  };

  const handleManageLarkNotifications = (admin) => {
    if (!hasPermission) return permissionError();
    if (!admin.larkUserId) {
      return alert(
        "Lark sign-in required",
        "This admin must sign in with Lark before notifications can be enabled.",
        "warning",
      );
    }
    setLarkNotificationAdmin(admin);
    setSelectedLarkNotificationTypes(admin.larkNotificationTypes || []);
  };

  const handleLarkNotificationTypeChange = (notificationType) => {
    setSelectedLarkNotificationTypes((notificationTypes) =>
      notificationTypes.includes(notificationType)
        ? notificationTypes.filter((type) => type !== notificationType)
        : [...notificationTypes, notificationType],
    );
  };

  const handleSaveLarkNotifications = () => {
    props.updateLarkNotifications(
      larkNotificationAdmin._id,
      selectedLarkNotificationTypes,
    );
    setLarkNotificationAdmin(null);
  };

  const larkNotificationLabels = (admin) => {
    const selectedTypes = admin?.larkNotificationTypes || [];
    if (!admin?.larkUserId) return "Lark sign-in required";
    if (!selectedTypes.length) return "None";
    return LARK_NOTIFICATION_TYPES
      .filter((notification) => selectedTypes.includes(notification.key))
      .map((notification) => notification.label)
      .join(", ");
  };

  // Calculate pagination
  const indexOfLastItem = activePage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3 className="mb-3 text-light" style={{ color: "#e4eeff" }}>
              Admin Management
            </h3>
          </div>
          <div className="col-12 col-md-6 order-md-2 order-first">
            <nav
              aria-label="breadcrumb"
              className="breadcrumb-header float-start float-lg-end"
            >
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/admin/dashboard" className="text-danger">
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Admin Management
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className="card" id="card">
            <div className="card-header pb-0">
              <div className="row my-3">
                <div className="col-xs-12 col-sm-12 col-md-6 col-lg-8 float-left">
                  <div className="text-left align-sm-left d-md-flex d-lg-flex justify-content-start">
                    <button
                      className="btn btn-primary"
                      style={{ marginRight: 10 }}
                      onClick={() => handleOpenDialog()}
                    >
                      <i className="fas fa-plus fa-md"></i>
                      <span className="ml-2">New Admin</span>
                    </button>
                  </div>
                </div>
                <div className="col-xs-12 col-sm-12 col-md-6 col-lg-4 float-right">
                  <form action="">
                    <div className="input-group mb-3 border rounded-pill">
                      <div className="input-group-prepend border-0">
                        <div id="button-addon4" className="btn text-danger">
                          <i className="fas fa-search mt-2"></i>
                        </div>
                      </div>
                      <input
                        type="search"
                        id="searchBar"
                        autoComplete="off"
                        placeholder="Search by name or email"
                        aria-describedby="button-addon4"
                        className="form-control bg-none border-0 rounded-pill searchBar"
                        value={search}
                        onChange={handleSearch}
                      />
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="card-body card-overflow pt-0">
              <table className="table table-striped mt-5 text-center">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>larkId</th>
                    <th>Created At</th>
                    <th>Enabled</th>
                    <th>Lark alerts</th>
                    <th>Access</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems?.length > 0 ? (
                    currentItems?.map((admin, index) => {
                      return (
                        <tr key={admin._id}>
                          <td>{indexOfFirstItem + index + 1}</td>
                          <td>
                            <img
                              height="50px"
                              width="50px"
                              alt="admin"
                              src={admin?.image ? admin?.image : Male}
                              style={{
                                boxShadow: "0 5px 15px 0 rgb(105 103 103 / 0%)",
                                border: "2px solid #fff",
                                borderRadius: 10,
                                objectFit: "cover",
                                display: "block",
                              }}
                              className="mx-auto"
                              onError={(e) => {
                                e.target.src = Male;
                              }}
                            />
                          </td>
                          <td>{admin?.name}</td>
                          <td>
                            {admin?.username || "—"}
                            {admin?.mustChangePassword && (
                              <div className="small text-warning">Temporary password</div>
                            )}
                          </td>
                          <td>{admin?.email}</td>
                          <td>{admin?.larkUserId || "N/A"}</td>
                          <td>
                            {dayjs(admin?.createdAt).format("DD MMM YYYY")}
                          </td>{" "}
                          <td>
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={!admin?.isDisabled}
                                onChange={() => handleToggleDisabled(admin._id)}
                                disabled={admin._id === currentAdminId}
                              />
                              <span className="slider">
                                <p
                                  style={{
                                    fontSize: 12,
                                    marginLeft: `${!admin?.isDisabled ? "-24px" : "35px"}`,
                                    color: "#000",
                                    marginTop: "6px",
                                  }}
                                >
                                  {admin?.isDisabled ? "NO" : "YES"}
                                </p>
                              </span>
                            </label>
                          </td>
                          <td>
                            <Tooltip
                              title={
                                admin?.larkUserId
                                  ? larkNotificationLabels(admin)
                                  : "Admin must sign in with Lark first"
                              }
                            >
                              <span>{larkNotificationLabels(admin)}</span>
                            </Tooltip>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary ml-2"
                              onClick={() => handleManageLarkNotifications(admin)}
                              disabled={!admin?.larkUserId}
                            >
                              Manage
                            </button>
                          </td>
                          <td>
                            <Tooltip title="Manage Access">
                              <button
                                type="button"
                                className="btn btn-sm btn-warning"
                                onClick={() => handleOpenAccessDialog(admin)}
                              >
                                <i className="fas fa-key fa-lg"></i>
                              </button>
                            </Tooltip>
                          </td>
                          <td>
                            <Tooltip title="Edit">
                              <button
                                type="button"
                                className="btn btn-sm btn-info mr-2"
                                onClick={() => handleOpenDialog(admin)}
                              >
                                <i className="fas fa-edit fa-lg"></i>
                              </button>
                            </Tooltip>
                            <Tooltip title="Reset sign-in (issue a temporary password)">
                              <button
                                type="button"
                                className="btn btn-sm btn-warning mr-2"
                                onClick={() => handleResetSignin(admin)}
                                disabled={admin._id === currentAdminId}
                              >
                                <i className="fas fa-unlock-alt fa-lg"></i>
                              </button>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(admin._id)}
                                disabled={admin._id === currentAdminId}
                              >
                                <i className="fas fa-trash fa-lg"></i>
                              </button>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={10} align="center">
                        Nothing to show!!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <Pagination
                activePage={activePage}
                rowsPerPage={rowsPerPage}
                userTotal={data.length}
                handleRowsPerPage={handleRowsPerPage}
                handlePageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </div>

      <AdminDialog />
      <AdminAccessDialog />
      <Dialog open={Boolean(issuedPassword)} onClose={() => setIssuedPassword(null)} fullWidth maxWidth="xs">
        <DialogTitle>Temporary password</DialogTitle>
        <DialogContent>{issuedPassword && <TemporaryPassword {...issuedPassword} />}</DialogContent>
        <DialogActions>
          <button type="button" className="btn btn-danger" onClick={() => setIssuedPassword(null)}>
            Done
          </button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(larkNotificationAdmin)}
        onClose={() => setLarkNotificationAdmin(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className="text-white">Lark alerts for {larkNotificationAdmin?.name}</DialogTitle>
        <DialogContent className="text-white">
          <p className="text-white">Select the Lark notifications this admin should receive.</p>
          {LARK_NOTIFICATION_TYPES.map((notification) => (
            <label key={notification.key} className="d-block mb-3 text-white">
              <input
                type="checkbox"
                className="mr-2"
                checked={selectedLarkNotificationTypes.includes(notification.key)}
                onChange={() => handleLarkNotificationTypeChange(notification.key)}
              />
              {notification.label}
            </label>
          ))}
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setLarkNotificationAdmin(null)}
          >
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSaveLarkNotifications}>
            Save
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default connect(null, {
  getAdmins,
  deleteAdmin,
  toggleAdminDisabled,
  updateLarkNotifications,
  resetAdminSignin,
})(
  AdminManagement,
);
